import { Rocket, Zap, Brain, Leaf, Heart, Globe, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

// Future jobs that will exist when today's middle schoolers are adults
const futureJobs = [
  {
    id: 'ai-trainer',
    title: 'AI Trainer',
    emoji: '🤖',
    color: 'from-blue-500 to-cyan-500',
    description: 'Teach artificial intelligence how to think and respond better',
    why: 'AI is everywhere but needs humans to train it to be helpful and safe',
    skills: ['Communication', 'Problem Solving', 'Tech Understanding'],
    coolFact: "You'd basically be a teacher for robots!"
  },
  {
    id: 'space-tourism',
    title: 'Space Tourism Guide',
    emoji: '🚀',
    color: 'from-purple-500 to-pink-500',
    description: 'Help tourists experience space travel safely and memorably',
    why: 'Companies like SpaceX are making space tourism real!',
    skills: ['Customer Service', 'Space Knowledge', 'Calm Under Pressure'],
    coolFact: 'You could work on a space station or lunar base!'
  },
  {
    id: 'climate-engineer',
    title: 'Climate Engineer',
    emoji: '🌍',
    color: 'from-green-500 to-emerald-500',
    description: 'Design solutions to help reverse climate change',
    why: 'We need brilliant minds to save our planet!',
    skills: ['Science', 'Engineering', 'Creativity'],
    coolFact: 'You might design giant machines that clean the air!'
  },
  {
    id: 'vr-world-builder',
    title: 'VR World Builder',
    emoji: '🎮',
    color: 'from-orange-500 to-red-500',
    description: 'Create immersive virtual reality experiences and worlds',
    why: 'VR is becoming the next big platform for games and work',
    skills: ['3D Design', 'Creativity', 'Programming'],
    coolFact: 'People will literally live in the worlds you create!'
  },
  {
    id: 'drone-traffic',
    title: 'Drone Traffic Controller',
    emoji: '🛸',
    color: 'from-amber-500 to-orange-500',
    description: 'Manage the sky highways where delivery drones fly',
    why: 'Millions of drones will fill our skies delivering packages',
    skills: ['Quick Thinking', 'Technology', 'Organization'],
    coolFact: "It's like air traffic control but for robots!"
  },
  {
    id: 'gene-designer',
    title: 'Genetic Designer',
    emoji: '🧬',
    color: 'from-pink-500 to-purple-500',
    description: 'Help prevent diseases by understanding and editing genes',
    why: 'Gene therapy could cure diseases before they happen',
    skills: ['Biology', 'Ethics', 'Precision'],
    coolFact: 'You could help people live longer, healthier lives!'
  },
  {
    id: 'robot-mechanic',
    title: 'Robot Mechanic',
    emoji: '🔧',
    color: 'from-slate-500 to-zinc-600',
    description: 'Repair and maintain the robots that work alongside humans',
    why: 'Robots will be everywhere - homes, hospitals, factories',
    skills: ['Mechanics', 'Electronics', 'Problem Solving'],
    coolFact: 'Like a car mechanic, but for robots!'
  },
  {
    id: 'digital-identity',
    title: 'Digital Identity Guard',
    emoji: '🔐',
    color: 'from-indigo-500 to-purple-500',
    description: "Protect people's online identity and digital presence",
    why: 'Our digital lives need protection from hackers',
    skills: ['Cybersecurity', 'Detective Skills', 'Technology'],
    coolFact: "You'd be like a superhero for the internet!"
  }
];

export default function MSFutureJobsPage() {
  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Rocket className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Jobs of the Future</h1>
            <p className="text-slate-300">Careers that might not exist yet... but will!</p>
          </div>
        </div>

        <div className="glass-card p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/30">
          <p className="text-purple-200 text-sm">
            <Zap className="w-4 h-4 inline mr-2" />
            By the time you're an adult, 65% of today's students will have jobs that don't exist yet!
          </p>
        </div>
      </motion.div>

      {/* Future Jobs Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {futureJobs.map((job, index) => (
          <motion.div
            key={job.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-card p-6 hover:scale-[1.02] transition-transform"
          >
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${job.color} flex items-center justify-center text-2xl`}>
                {job.emoji}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{job.title}</h3>
                <p className="text-slate-300 text-sm mt-1">{job.description}</p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
              <p className="text-slate-300 text-sm">
                <strong className="text-white">Why it will exist:</strong> {job.why}
              </p>
            </div>

            <div className="mt-3">
              <p className="text-slate-300 text-xs mb-2">Skills needed:</p>
              <div className="flex flex-wrap gap-1">
                {job.skills.map((skill, i) => (
                  <span key={i} className="px-2 py-1 bg-slate-700 rounded-full text-xs text-slate-300">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-4 p-3 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg border border-yellow-500/20">
              <p className="text-yellow-200 text-sm">
                ✨ {job.coolFact}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-8 text-center"
      >
        <p className="text-slate-300">
          The future is full of possibilities. Keep learning and stay curious! 🚀
        </p>
      </motion.div>
    </div>
  );
}
